import type { ApiResponse } from '../types';
import type { INotification } from '../../types/interfaces/IUI';

type ApiLikeError = {
  success?: boolean;
  message?: string;
  userMessage?: string;
  errors?: string[];
  error?: string;
  status?: number;
  statusCode?: number;
  httpStatus?: number;
  code?: string;
  category?: string;
  uiHint?: string;
  severity?: string;
  retryable?: boolean;
  fieldErrors?: Record<string, string[]> | null;
  traceId?: string | null;
};

export type ErrorDisplayMode = 'inline' | 'toast' | 'page' | 'modal' | 'silent';

export interface ApiErrorDecision {
  mode: ErrorDisplayMode;
  title: string;
  message: string;
  fieldErrors: Record<string, string[]>;
  status: number;
  code?: string;
  traceId?: string | null;
  retryable?: boolean;
  severity?: string;
}

interface ResolveErrorOptions {
  fallbackMessage?: string;
  context?: 'login' | 'form' | 'protected' | 'module';
}

const DEFAULT_MESSAGE = 'Ocurrio un error inesperado. Intenta nuevamente.';

const toFieldErrors = (fieldErrors?: Record<string, string[]> | null) => {
  if (!fieldErrors) return {};
  const output: Record<string, string[]> = {};
  for (const key in fieldErrors) {
    if (!Object.prototype.hasOwnProperty.call(fieldErrors, key)) continue;
    const value = fieldErrors[key];
    if (Array.isArray(value) && value.length > 0) {
      output[key] = value;
    }
  }
  return output;
};

const toInlineMap = (fieldErrors: Record<string, string[]>) => {
  const output: Record<string, string> = {};
  for (const field in fieldErrors) {
    if (!Object.prototype.hasOwnProperty.call(fieldErrors, field)) continue;
    const errors = fieldErrors[field];
    if (Array.isArray(errors) && errors.length > 0) {
      output[field] = errors[0];
    }
  }
  return output;
};

const statusTitle = (status: number): string => {
  switch (status) {
    case 400:
      return 'Solicitud inválida';
    case 401:
      return 'No autenticado';
    case 403:
      return 'Sin permisos';
    case 404:
      return 'No encontrado';
    case 409:
      return 'Conflicto';
    case 422:
      return 'Validación';
    case 500:
      return 'Error del servidor';
    default:
      return 'Error';
  }
};

export const resolveApiErrorDecision = <T>(
  response: ApiResponse<T> | ApiLikeError | null | undefined,
  options: ResolveErrorOptions = {}
): ApiErrorDecision => {
  const status = response?.httpStatus || response?.statusCode || response?.status || 500;
  const code = response?.code;
  const fieldErrors = toFieldErrors(response?.fieldErrors);

  const message =
    response?.userMessage ||
    response?.message ||
    response?.error ||
    response?.errors?.[0] ||
    options.fallbackMessage ||
    DEFAULT_MESSAGE;

  // Prioridad 1: fieldErrors
  if (Object.keys(fieldErrors).length > 0) {
    return {
      mode: 'inline',
      title: 'Revisa los datos ingresados',
      message,
      fieldErrors,
      status,
      code,
      traceId: response?.traceId || null,
      retryable: response?.retryable,
    };
  }

  // Prioridad 2: uiHint
  const uiHint = response?.uiHint;
  if (uiHint && ['inline', 'toast', 'page', 'modal', 'silent'].includes(uiHint)) {
    return {
      mode: uiHint as ErrorDisplayMode,
      title: statusTitle(status),
      message,
      fieldErrors,
      status,
      code,
      traceId: response?.traceId || null,
      retryable: response?.retryable,
    };
  }

  // Priorizacion adicional por contexto/codigo de auth en login
  if (
    options.context === 'login' &&
    (code === 'AUTH_INVALID_CREDENTIALS' || code === 'AUTH_USER_INACTIVE')
  ) {
    return {
      mode: 'inline',
      title: 'Credenciales',
      message,
      fieldErrors,
      status,
      code,
      traceId: response?.traceId || null,
      retryable: response?.retryable,
    };
  }

  // Prioridad 3: category
  switch (response?.category) {
    case 'validation':
      return {
        mode: options.context === 'form' || options.context === 'login' ? 'inline' : 'toast',
        title: 'Validación',
        message,
        fieldErrors,
        status,
        code,
        traceId: response?.traceId || null,
        retryable: response?.retryable,
      };
    case 'auth':
      if (code === 'AUTH_TOKEN_INVALID' || code === 'AUTH_SESSION_INVALID') {
        return {
          mode: 'silent',
          title: 'Sesión inválida',
          message,
          fieldErrors,
          status,
          code,
          traceId: response?.traceId || null,
          retryable: response?.retryable,
        };
      }
      return {
        mode: 'toast',
        title: 'Sesión',
        message,
        fieldErrors,
        status,
        code,
        traceId: response?.traceId || null,
        retryable: response?.retryable,
      };
    case 'business':
    case 'permission':
    case 'notFound':
    case 'system':
      return {
        mode: 'toast',
        title: statusTitle(status),
        message,
        fieldErrors,
        status,
        code,
        traceId: response?.traceId || null,
        retryable: response?.retryable,
      };
    default:
      break;
  }

  // Prioridad 4: fallback por status
  if (status === 422 || status === 400) {
    return {
      mode: options.context === 'form' || options.context === 'login' ? 'inline' : 'toast',
      title: 'Validación',
      message,
      fieldErrors,
      status,
      code,
      traceId: response?.traceId || null,
      retryable: response?.retryable,
    };
  }

  return {
    mode: 'toast',
    title: statusTitle(status),
    message,
    fieldErrors,
    status,
    code,
    traceId: response?.traceId || null,
    retryable: response?.retryable,
  };
};

export const toInlineFieldMap = (fieldErrors: Record<string, string[]>) =>
  toInlineMap(fieldErrors);

export const createNotificationFromDecision = (
  decision: ApiErrorDecision
): Omit<INotification, 'id'> | null => {
  if (decision.mode === 'silent' || decision.mode === 'inline') {
    return null;
  }

  return {
    type: decision.severity === 'warning' ? 'warning' : 'error',
    title: decision.title,
    message: decision.message,
  };
};

export const notifyApiError = <T>(
  addNotification: (notification: Omit<INotification, 'id'>) => void,
  response: ApiResponse<T> | ApiLikeError | null | undefined,
  options: ResolveErrorOptions = {}
): ApiErrorDecision => {
  const decision = resolveApiErrorDecision(response, options);
  const notification = createNotificationFromDecision(decision);
  if (notification) {
    addNotification(notification);
  }
  return decision;
};
